/**
 * Interfaz que define el contrato de los errores devueltos por el backend.
 * * Permite mapear de forma segura la estructura JSON de error que utiliza la API,
 * facilitando el acceso a códigos técnicos y marcas de tiempo del servidor.
 */
export interface ApiError {
  /** Indica si la operación fue fallida (generalmente false). */
  success: boolean;
  /** Estado semántico del error (ej. 'error', 'fail'). */
  status: string;
  /** Código identificador único del error definido en el catálogo del backend. */
  errorCode: string;
  /** Descripción técnica del error enviada por el servidor. */
  message: string;
  /** Marca de tiempo en la que ocurrió el error en el servidor. */
  timestamp?: string;
}

/**
 * Interfaz para representar errores normalizados dentro de la aplicación.
 * * Esta estructura unifica errores de red (HTTP), errores de servidor (API) y
 * errores lógicos del cliente, permitiendo que los componentes de UI consuman
 * una interfaz predecible y amigable para el usuario.
 */
export interface AppError {
  /** Código identificador único del error (interno o de API). */
  errorCode: string;
  /** Mensaje de error localizado y procesado para el usuario final. */
  message: string;
  /** Código de estado HTTP (0 para errores de conexión). */
  httpStatus: number;
  /** Momento exacto en el que el error fue capturado por el cliente. */
  timestamp: Date;
  /** URL de la petición que originó el fallo, si aplica. */
  url?: string;
}

/** * Categorías semánticas de errores basadas en su naturaleza.
 * * Ayuda al `ErrorHandlerService` a decidir acciones automáticas como
 * redirecciones al login o limpiezas de caché.
 */
export type ErrorPrefix = 'AUTH' | 'VAL' | 'PERM' | 'NOT' | 'SRV' | 'BIZ' | 'NETWORK' | 'UNKNOWN';

/**
 * Extrae la categoría (prefijo) de un error a partir de su código identificador.
 * * Optimizado para identificar el tipo de error mediante el análisis de la primera
 * parte del código (antes del guion bajo). Facilita la toma de decisiones lógica
 * en el manejo global de excepciones.
 * * @param errorCode El código de error completo (ej. 'AUTH_003').
 * @returns El prefijo categorizado de tipo `ErrorPrefix`.
 */
export function getErrorPrefix(errorCode: string): ErrorPrefix {
  const prefix = errorCode.split('_')[0] as ErrorPrefix;
  const validPrefixes: ErrorPrefix[] = ['AUTH', 'VAL', 'PERM', 'NOT', 'SRV', 'BIZ'];

  if (validPrefixes.includes(prefix)) return prefix;
  if (errorCode === 'NETWORK') return 'NETWORK';
  return 'UNKNOWN';
}

/** * Diccionario centralizado de mensajes de error en español.
 * * Mapea códigos técnicos (`errorCode`) a mensajes descriptivos para el usuario.
 * Centralizar esto permite una fácil internacionalización o cambios de redacción.
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // Autenticación
  AUTH_001: 'No se proporcionó token de acceso',
  AUTH_002: 'Token de acceso inválido',
  AUTH_003: 'Tu sesión ha expirado. Inicia sesión nuevamente',
  AUTH_004: 'Email o contraseña incorrectos',
  AUTH_005: 'Debes verificar tu correo antes de continuar',
  AUTH_006: 'Error con la autenticación de Google',
  AUTH_007: 'Sesión expirada. Inicia sesión nuevamente',
  AUTH_008: 'Sesión inválida. Inicia sesión nuevamente',
  AUTH_009: 'Usuario no encontrado',
  AUTH_010: 'Plataforma no soportada',

  // Validación
  VAL_001: 'Faltan campos obligatorios',
  VAL_002: 'El formato de fecha no es válido',
  VAL_003: 'El slot seleccionado no es válido',
  VAL_004: 'Las horas deben estar entre 0 y 24',
  VAL_005: 'Ya existe un registro con estos datos',
  VAL_006: 'Tipo de dato no válido',
  VAL_007: 'El formato de color no es válido (use #XXXXXX)',
  VAL_008: 'Los datos proporcionados no son válidos',
  VAL_009: 'Se esperaba una lista de elementos',
  VAL_010: 'El patrón no tiene turnos configurados',

  // Permisos
  PERM_001: 'No tienes permiso para realizar esta acción',
  PERM_002: 'No tienes permiso para crear este recurso',
  PERM_003: 'No tienes permiso para modificar este recurso',
  PERM_004: 'No tienes permiso para eliminar este recurso',
  PERM_005: 'Debes iniciar sesión para continuar',

  // No encontrado
  NOT_001: 'La página solicitada no existe',
  NOT_002: 'Usuario no encontrado',
  NOT_003: 'Categoría no encontrada',
  NOT_004: 'Noticia no encontrada',
  NOT_005: 'Card no encontrada',
  NOT_006: 'Flujo no encontrado',
  NOT_007: 'Turno no encontrado',
  NOT_008: 'Asignación no encontrada',
  NOT_009: 'Configuración no encontrada',
  NOT_010: 'Cuenta conectada no encontrada',
  NOT_011: 'Plantilla no encontrada',
  NOT_012: 'Patrón no encontrado',
  NOT_013: 'Producto no encontrado',

  // Servidor
  SRV_001: 'Error del servidor. Intenta más tarde',
  SRV_002: 'Error de base de datos',
  SRV_003: 'Error al conectar con servicio externo',
  SRV_004: 'Error al enviar el correo',
  SRV_005: 'Error al generar el código de acceso',

  // Negocio
  BIZ_001: 'El código ingresado no es válido',
  BIZ_002: 'El código ha expirado. Solicita uno nuevo',
  BIZ_003: 'No se pudo guardar la información',
  BIZ_004: 'El usuario ya existe en el sistema',

  // Errores de red y desconocidos
  NETWORK: 'Error de conexión. Verifica tu conexión a internet',
  UNKNOWN: 'Ha ocurrido un error inesperado',
};

/**
 * Recupera el mensaje localizado correspondiente a un código de error.
 * * Mejora: Se ha aplicado tipado estricto para que el compilador verifique que el
 * código de error existe en el diccionario `ERROR_MESSAGES`.
 * * @param errorCode El código de error (debe coincidir con las claves del diccionario).
 * @param fallback Mensaje opcional de respaldo si el código no se encuentra.
 * @returns El mensaje traducido o un mensaje de error genérico.
 * * @example
 * const errorMsg = getErrorMessage('AUTH_003'); // "Tu sesión ha expirado..."
 */
export function getErrorMessage(
  errorCode: keyof typeof ERROR_MESSAGES | string,
  fallback?: string,
): string {
  return (
    ERROR_MESSAGES[errorCode as keyof typeof ERROR_MESSAGES] ?? fallback ?? 'Ha ocurrido un error'
  );
}
